import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  key: string;
}

interface AgcoDocumentCacheDB extends DBSchema {
  'documents-cache': {
    key: string;
    value: CacheEntry<any>;
  };
  'images-cache': {
    key: string;
    value: {
      blob: Blob;
      timestamp: number;
    };
  };
}

const DB_NAME = 'agco-lisaprint-cache';
const DB_VERSION = 1;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

let dbInstance: IDBPDatabase<AgcoDocumentCacheDB> | null = null;

/**
 * Initialise et retourne l'instance de la base de données IndexedDB
 */
async function getDB(): Promise<IDBPDatabase<AgcoDocumentCacheDB>> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<AgcoDocumentCacheDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Store pour les documents
      if (!db.objectStoreNames.contains('documents-cache')) {
        db.createObjectStore('documents-cache', { keyPath: 'key' });
      }

      // Store pour les images (optionnel pour cache d'images)
      if (!db.objectStoreNames.contains('images-cache')) {
        db.createObjectStore('images-cache');
      }
    },
  });

  return dbInstance;
}

/**
 * Récupère une entrée du cache
 */
export async function getCacheEntry<T>(key: string): Promise<T | null> {
  try {
    const db = await getDB();
    const entry = await db.get('documents-cache', key);

    if (!entry) {
      return null;
    }

    // Vérifier si le cache est encore valide
    const now = Date.now();
    if (now - entry.timestamp > CACHE_DURATION) {
      // Cache expiré, le supprimer
      await db.delete('documents-cache', key);
      return null;
    }

    return entry.data as T;
  } catch (error) {
    console.error('Erreur lors de la récupération du cache:', error);
    return null;
  }
}

/**
 * Enregistre une entrée dans le cache
 */
export async function setCacheEntry<T>(key: string, data: T): Promise<void> {
  try {
    const db = await getDB();
    const entry: CacheEntry<T> = {
      key,
      data,
      timestamp: Date.now(),
    };

    await db.put('documents-cache', entry);
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement dans le cache:', error);
  }
}

/**
 * Supprime une entrée spécifique du cache
 */
export async function deleteCacheEntry(key: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete('documents-cache', key);
  } catch (error) {
    console.error('Erreur lors de la suppression du cache:', error);
  }
}

/**
 * Vide tout le cache
 */
export async function clearCache(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear('documents-cache');
    await db.clear('images-cache');
  } catch (error) {
    console.error('Erreur lors du nettoyage du cache:', error);
  }
}

/**
 * Nettoie les entrées expirées du cache
 */
export async function cleanExpiredCache(): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction('documents-cache', 'readwrite');
    const store = tx.objectStore('documents-cache');
    const allEntries = await store.getAll();
    const now = Date.now();

    for (const entry of allEntries) {
      if (now - entry.timestamp > CACHE_DURATION) {
        await store.delete(entry.key);
      }
    }

    await tx.done;
  } catch (error) {
    console.error('Erreur lors du nettoyage des caches expirés:', error);
  }
}

/**
 * Génère une clé de cache pour les documents
 */
export function generateCacheKey(params: {
  page?: number;
  brand?: string;
  type?: string;
}): string {
  const { page = 1, brand = 'all', type = 'agco' } = params;
  return `${type}-page-${page}-brand-${brand}`;
}
